import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Box, Button, CircularProgress, FormHelperText, LinearProgress } from "@mui/material";
import { useRef, type FC } from "react";
import { EmailEditor as UnlayerEmailEditor } from "react-email-editor";
import { SimpleSaver, type SimpleSavable } from "../../generic/components/simple-saver";
import type { UnlayerMergeTags } from "../../merge-tag/util";
import { useUnlayerEditor } from "../hooks/use-unlayer-editor";
import { useUnlayerEditorOptions } from "../hooks/use-unlayer-editor-options";
import type { UnlayerEditor } from "../types/editor";
import type { UnlayerDesign } from "../types/unlayer-design";
import { DisplayConditionDialog } from "./display-condition-dialog";

declare global {
  interface Window {
    unlayer: {
      createEditor: (options: unknown) => UnlayerEditor;
    };
  }
}

export const EmailEditor: FC<{
  design: UnlayerDesign | null;
  mergeTags: UnlayerMergeTags;
  disabled?: boolean;
  error?: string;
  onPlainTextChange?: (text: string) => void;
  isSurveyTrigger?: boolean;
  insertSurveyBlockOnMount?: boolean;
}> = ({
  design,
  mergeTags,
  disabled,
  error,
  onPlainTextChange,
  isSurveyTrigger = false,
  insertSurveyBlockOnMount = false,
}) => {
  const { editor, onReady, save, exportPdf, exportingPdf, displayCondition, dirty, reset } =
    useUnlayerEditor(design, { onPlainTextChange, isSurveyTrigger, insertSurveyBlockOnMount });
  const { loading, data: options } = useUnlayerEditorOptions(mergeTags, isSurveyTrigger);
  const saverRef = useRef<SimpleSavable>(null);

  if (loading) return <LinearProgress />;

  return (
    <>
      <div style={{ flex: 1, display: "flex", minHeight: 750, marginLeft: -64, marginRight: -64 }}>
        <UnlayerEmailEditor
          key={String(isSurveyTrigger)}
          ref={editor}
          onReady={onReady}
          style={
            disabled
              ? { pointerEvents: "none", opacity: 0.6, filter: "grayscale(100%)" }
              : undefined
          }
          options={options}
        />

        <SimpleSaver ref={saverRef} save={save} dirty={dirty} reset={reset} />

        {displayCondition.isOpen && (
          <DisplayConditionDialog
            open={displayCondition.isOpen}
            onClose={displayCondition.onClose}
            onConfirm={displayCondition.onConfirm}
            selectedId={displayCondition.selectedId}
            nestedBlocked={displayCondition.nestedBlocked}
          />
        )}
      </div>
      <Box sx={{ mt: 1, ml: 1 }}>
        <Button
          variant="outlined"
          startIcon={exportingPdf ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
          onClick={exportPdf}
          disabled={exportingPdf}
          data-analytics-id="export-unlayer-pdf"
        >
          {exportingPdf ? "Exporting..." : "Export PDF"}
        </Button>
      </Box>
      {error && (
        <FormHelperText error variant="filled">
          {error}
        </FormHelperText>
      )}
    </>
  );
};
